import React, { Fragment, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Container from '@careers-wales/react-components/dist/components/layout/Container/Container.js';
import Row from '@careers-wales/react-components/dist/components/layout/Row/Row.js';
import Column from '@careers-wales/react-components/dist/components/layout/Column/Column.js';
import Counter from '@careers-wales/react-components/dist/components/results/Counter/Counter.js';
import Sanitise from '@careers-wales/react-components/dist/components/utilities/Sanitise/Sanitise.js';
import SubHeading from '@careers-wales/react-components/dist/components/typography/Subheading/Subheading.js';
import HeroBlock from '@careers-wales/react-components/dist/components/banners/HeroBlock/HeroBlock.js';
import CallToActionLink from '@careers-wales/react-components/dist/components/links/CallToActionLink/CallToActionLink.js';
import PrimaryLink from '@careers-wales/react-components/dist/components/links/PrimaryLink/PrimaryLink.js';
import Divider from '@careers-wales/react-components/dist/components/misc/Divider/Divider.js';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';

function DetailView({ event, countSessions }) {
  const { t } = useTranslation();

  useEffect(() => {
    window.initialiseModal();
    window.initialiseAccordion(t('styleguide:expand_all'), t('styleguide:close_all'));
    window.initialiseDocumentDownload();
    window.initialiseCardInfographic();
    window.initialiseVideo();
    window.initialiseShowMoreLess(
      t('styleguide:show_more'),
      t('styleguide:show_less'),
      t('styleguide:expand_all'),
      t('styleguide:close_all')
    );
    window.initialiseAnchor(
      t('styleguide:document_email_link'),
      t('styleguide:opens_email_client'),
      t('styleguide:link_to'),
      t('styleguide:external_website')
    );
    window.initialiseTables();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        <title>
          {event.title} | {t('app.name')}
        </title>
      </Helmet>

      <BreadcrumbsItem to={event.alias}>{event.title}</BreadcrumbsItem>

      <Container>
        <SubHeading type="sub-heading-small" colour={'grey'} text={t('view.detail.subtitle')} />
        <h1 style={{ marginTop: '-15px' }}>{event.title}</h1>
      </Container>

      <HeroBlock
        meta={
          event.sessions.length
            ? [
                {
                  key:
                    event.dates.length === 2 ? (
                      <strong>
                        {event.dates[0]} {t('styleguide:and')} {event.dates[1]}
                      </strong>
                    ) : (
                      <strong>{event.dates[0]}</strong>
                    ),
                  value:
                    event.dates.length > 2 ? (
                      <a href={'#prices-times'}>
                        + {event.dates.length - 1} {t('view.detail.otherDates')}
                      </a>
                    ) : null
                },
                {
                  key:
                    event.locations.length === 2 ? (
                      <strong>
                        {event.locations[0]} {t('styleguide:and')} {event.locations[1]}
                      </strong>
                    ) : (
                      <strong>{event.locations[0]}</strong>
                    ),
                  value:
                    event.locations.length > 2 ? (
                      <a href={'#prices-times'}>
                        + {event.locations.length - 1} {t('view.detail.otherLocations')}
                      </a>
                    ) : null
                }
              ]
            : []
        }
        suffix={
          event.sessions.length > 0 ? (
            <CallToActionLink size="medium" title={t('view.detail.bookingDetails')} href={'#prices-times'}>
              {t('view.detail.bookingDetails')}
            </CallToActionLink>
          ) : (
            ''
          )
        }
      >
        <Sanitise string={event.description} />
      </HeroBlock>

      <Container>
        {event.image && <img src={event.image} alt={event.title} />}

        {event.details && <Sanitise string={event.details} />}

        {event.content_above && <div dangerouslySetInnerHTML={{ __html: event.content_above }} />}

        {event.sessions.length > 0 && (
          <div id="prices-times">
            <h2>{event.sessionsHeading}</h2>
            <Counter
              value={countSessions(event)}
              label={countSessions(event) === 1 ? t('view.detail.session') : t('view.detail.sessions')}
            />
            {event.pricesTimes && <Sanitise string={event.pricesTimes} />}

            {event.sessions.map((session_data, index) => {
              return session_data.sessions.map((session, session_index) => {
                let booking;
                if (session.url) {
                  booking = (
                    <PrimaryLink
                      href={session.url}
                      title={session.call_to_action ? session.call_to_action : t('misc.bookOnline')}
                      hiddenText={session.call_to_action_hidden}
                    >
                      {session.call_to_action ? session.call_to_action : t('misc.bookOnline')}
                    </PrimaryLink>
                  );
                }

                if (session.email) {
                  booking = (
                    <PrimaryLink
                      href={
                        'mailto:' +
                        session.email +
                        '?subject=' +
                        t('misc.bookingFor') +
                        ': ' +
                        event.title +
                        '&body=' +
                        t('misc.name') +
                        ' ' +
                        t('misc.ofEvent') +
                        ': ' +
                        event.title +
                        '%0d%0a' +
                        t('misc.date') +
                        ' ' +
                        t('misc.ofEvent') +
                        ': ' +
                        session.date +
                        '%0d%0a' +
                        t('misc.time') +
                        ' ' +
                        t('misc.ofEvent') +
                        ': ' +
                        session.time +
                        '%0d%0a'
                      }
                      data-link-hidden-text={session.call_to_action_hidden}
                    >
                      {session.call_to_action ? session.call_to_action : t('misc.contactUs')}
                    </PrimaryLink>
                  );
                }

                return (
                  <Fragment key={session_index}>
                    {session_index === 0 && event.sessions.length > 1 ? (
                      <Row>
                        <Column md={10} sm={9} xs={12}>
                          <h3 class={'gel-xs-mt-0'}>{session_data.location}</h3>
                        </Column>
                      </Row>
                    ) : null}
                    {session.title && (
                      <Row>
                        {/*<Column md={2}>*/}
                        {/*  <strong>{t('view.detail.title')}:</strong>*/}
                        {/*</Column>*/}
                        <Column md={12}>
                          <h4 className={'gel-xs-mt-0'}>{session.title}</h4>
                        </Column>
                      </Row>
                    )}
                    <Row className={'gel-xs-mb-20'}>
                      <Column md={2} sm={3} xs={12}>
                        <strong>{t('view.detail.date')}:</strong>
                      </Column>
                      <Column md={10} sm={9} xs={12}>
                        {session.date}
                      </Column>
                    </Row>
                    <Row className={'gel-xs-mb-20'}>
                      <Column md={2} sm={3} xs={12}>
                        <strong>{t('view.detail.time')}:</strong>
                      </Column>
                      <Column md={10} sm={9} xs={12}>
                        {session.time}
                      </Column>
                    </Row>

                    {event.sessions.length === 1 && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.location')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session_data.location}
                        </Column>
                      </Row>
                    )}

                    {session.status.length > 0 && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.status')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.status.join(', ')}
                        </Column>
                      </Row>
                    )}

                    {session.year_groups.length > 0 && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.year_groups')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.year_groups.join(', ')}
                        </Column>
                      </Row>
                    )}

                    {session.focus.length > 0 && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.focus')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.focus.join(', ')}
                        </Column>
                      </Row>
                    )}
                    {session.venue && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.venue')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.venue}
                        </Column>
                      </Row>
                    )}
                    {session.age.length > 0 && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.age')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.age.join(', ')}
                        </Column>
                      </Row>
                    )}

                    {session.cost && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.cost')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          {session.cost}
                        </Column>
                      </Row>
                    )}
                    {session.privacy && (
                      <Row className={'gel-xs-mb-20'}>
                        <Column md={2} sm={3} xs={12}>
                          <strong>{t('view.detail.privacy')}:</strong>
                        </Column>
                        <Column md={10} sm={9} xs={12}>
                          <Sanitise className={'last-child-no-margin'} string={session.privacy} />
                        </Column>
                      </Row>
                    )}
                    {session.fields.map((field, field_index) => {
                      return (
                        <Row className={'gel-xs-mb-20'}>
                          <Column md={2} sm={3} xs={12}>
                            <strong>{field.label}:</strong>
                          </Column>
                          <Column md={10} sm={9} xs={12}>
                            <Sanitise className={'last-child-no-margin'} string={field.content} />
                          </Column>
                        </Row>
                      );
                    })}

                    {booking && (
                      <Row>
                        <Column md={12}>{booking}</Column>
                      </Row>
                    )}

                    <Divider />
                  </Fragment>
                );
              });
            })}
          </div>
        )}

        {event.venueInformation && (
          <>
            <h2>{t('view.detail.venueInformation')}</h2>
            <Sanitise string={event.venueInformation} />
          </>
        )}
        {event.content && (
          <>
            <div dangerouslySetInnerHTML={{ __html: event.content }} />
          </>
        )}
      </Container>
    </>
  );
}

export default DetailView;
